import { AppSubject } from 'src/config/authorization.config';
import { environment } from 'src/environments/environment';
import { GenericTableSize } from '../types/misc/GenericTableTypes/GenericTableSize';
import { RowSelectionType } from '../types/misc/GenericTableTypes/RowSelectionTypes';
import { GenericActionConfiguration } from './GenericActionConfiguration';
import { GenericColumnConfiguration } from './GenericColumnConfiguration';
import { GenericRowActionConfiguration } from './GenericRowActionConfiguration';

/**
 * Configuration class for a generic table.
 */
export class GenericTableConfiguration {
  /**
   * The name of the table.
   */
  table!: string;

  /**
   * The size of the tables rows.
   */
  size?: GenericTableSize = 'md';

  /**
   * The default field to sort by.
   */
  defaultSortField?: string;

  /**
   * The default sort order (1 for ascending, -1 for descending).
   */
  defaultSortOrder: 1 | -1 = 1;

  /**
   * Indicates if grid lines of the table should be shown.
   */
  showGridLines?: boolean;

  /**
   * Indicates if striped rows should be shown. (alternating row colors)
   */
  showStripedRows?: boolean;

  /**
   * Indicates if the global filter input should be shown.
   */
  showGlobalFilter?: boolean;

  /**
   * The options for the number of rows per page.
   */
  rowsPerPageOptions?: number[];

  /**
   * The initial number of rows to display.
   */
  initialRowsCount?: number;

  /**
   * The translation key for the table.
   */
  translationKey!: string;

  /**
   * The configuration for the columns of the table.
   */
  columns!: GenericColumnConfiguration[];

  /**
   * The configuration for the row actions of the table.
   */
  rowActions!: GenericRowActionConfiguration[];

  /**
   * The width of the row action column in rem units.
   */
  rowActionWidthRem?: number;

  /**
   * The configuration for the top right action.
   * Usually used for the "Create" action.
   */
  topRightAction?: GenericActionConfiguration;

  /**
   * The configuration for the bottom right action.
   */
  bottomRightAction?: GenericActionConfiguration;

  /**
   * The configuration for the bottom left action.
   * Usually used for export actions.
   */
  bottomLeftAction?: GenericActionConfiguration;

  /**
   * Indicates if the CSV export action should be shown.
   */
  showCsvExportAction?: boolean;

  /**
   * Indicates if the Excel export action should be shown.
   */
  showExcelExportAction?: boolean;

  /**
   * Indicates if the PDF export action should be shown.
   */
  showPdfExportAction?: boolean;

  /**
   * The mode for row selection.
   */
  rowSelectionMode?: RowSelectionType;

  /**
   * Indicates if the summary should be shown.
   */
  showSummary?: boolean;

  /**
   * Indicates if lazy loading should be used.
   */
  lazyLoad!: boolean;

  /**
   * Indicates if the built-in service should be used.
   */
  useBuiltInService!: boolean;

  /**
   * Indicates if the table should be shown as a card.
   */
  showAsCard!: boolean;

  /**
   * Indicates if the filter configuration should be shown.
   */
  showFilterConfiguration!: boolean;

  /**
   * The style of the table.
   * @type {any}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableStyle: any;

  /**
   * The context for file downloads.
   */
  fileDownloadContext?: string;

  /**
   * The permission subject for the table.
   */
  permissionSubject?: AppSubject;

  /**
   * Constructs a new instance of GenericTableConfiguration.
   *
   * @param {Partial<GenericTableConfiguration>} [config] - An optional partial configuration object to initialize the instance.
   */
  constructor(config?: Partial<GenericTableConfiguration>) {
    Object.assign(this, config);

    if (!this.rowsPerPageOptions) {
      this.rowsPerPageOptions =
        environment.tableConfiguration.rowsPerPageOptions;
    }

    if (!this.initialRowsCount) {
      // eslint-disable-next-line prefer-destructuring
      this.initialRowsCount = this.rowsPerPageOptions[0];
    }

    this.showGridLines = this.showGridLines ?? false;
    this.showStripedRows = this.showStripedRows ?? false;
    this.showGlobalFilter = this.showGlobalFilter ?? true;
    this.showSummary = this.showSummary ?? false;
    this.lazyLoad = this.lazyLoad ?? false;
    this.rowActions = this.rowActions ?? [];
    this.useBuiltInService = this.useBuiltInService ?? true;
    this.showAsCard = this.showAsCard ?? true;
    this.tableStyle = this.tableStyle ?? { 'min-width': '50rem' };
    this.showFilterConfiguration = this.showFilterConfiguration ?? true;
  }
}
